/**
 * GraphQL type definitions matching backend schema — businessCapability pillar.
 * Split from graphql/types.ts (issue #1409).
 */

import type { PageInfo } from './common';

export enum CapabilityCriticality {
  Critical = 'critical',
  Important = 'important',
  Supporting = 'supporting',
  NiceToHave = 'nice_to_have',
}

export enum StrategicImportance {
  Strategic = 'strategic',
  Core = 'core',
  Supporting = 'supporting',
}

export enum CapabilityLifecycleStatus {
  Planned = 'planned',
  Developing = 'developing',
  Active = 'active',
  Mature = 'mature',
  Deprecated = 'deprecated',
  Retired = 'retired',
}

export enum InvestmentPriority {
  High = 'high',
  Medium = 'medium',
  Low = 'low',
  Hold = 'hold',
}

export enum InvestmentLevel {
  None = 'none',
  Maintain = 'maintain',
  Enhance = 'enhance',
  Transform = 'transform',
}

/**
 * Shallow child-capability summary returned by `BusinessCapability.children`
 * (issue #1465). Deliberately not the full `BusinessCapability` shape — the
 * detail-page query only needs enough fields to list and link to each child,
 * and fetching the full recursive fragment would pull each child's own
 * children/full field set for no UI benefit.
 */
export interface BusinessCapabilityChildSummary {
  entityId: string;
  name: string;
  criticality: CapabilityCriticality;
  lifecycleStatus: CapabilityLifecycleStatus;
  level: number;
}

/**
 * Shallow service summary returned by `BusinessCapability.supportingServices`
 * (issue #1495). Mirrors `BusinessCapabilityChildSummary` — only enough
 * fields to list and link to each supporting service.
 */
export interface BusinessCapabilityServiceSummary {
  entityId: string;
  name: string;
}

export interface BusinessCapability {
  entityId: string;
  entityType: string;
  name: string;
  description?: string | null;
  level: number;
  parentCapabilityId?: string | null;
  criticality: CapabilityCriticality;
  strategicImportance: StrategicImportance;
  lifecycleStatus: CapabilityLifecycleStatus;
  investmentPriority?: InvestmentPriority | null;
  businessOwnerId: string;
  owningBusinessUnitId: string;
  stakeholderIds: string[];
  maturityLevel?: number | null;
  targetMaturityLevel?: number | null;
  currentInvestmentLevel?: InvestmentLevel | null;
  targetInvestmentLevel?: InvestmentLevel | null;
  planningHorizon?: string | null;
  notes?: string | null;
  assumptions?: string | null;
  tags: string[];
  children?: BusinessCapabilityChildSummary[];
  supportingServices?: BusinessCapabilityServiceSummary[];
  serviceCount?: number;
  _schemaVersion: string;
  _createdAt: string;
  _createdBy: string;
  _updatedAt: string;
  _updatedBy: string;
  _deletedAt?: string | null;
}

export interface BusinessCapabilityEdge {
  node: BusinessCapability;
  cursor: string;
}

export interface BusinessCapabilityConnection {
  edges: BusinessCapabilityEdge[];
  pageInfo: PageInfo;
  totalCount?: number | null;
}

export interface BusinessCapabilityFilter {
  criticality?: CapabilityCriticality;
  strategicImportance?: StrategicImportance;
  lifecycleStatus?: CapabilityLifecycleStatus;
  level?: number;
  owningBusinessUnitId?: string;
  includeDeleted?: boolean;
}
